import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  addProjectContact,
  approveProjectChangeOrder,
  createProjectChangeOrder,
  createPhase,
  createPhaseAdjustment,
  createProject,
  createRoom,
  createRoomAdjustment,
  deletePhase,
  deletePhaseAdjustment,
  deleteProject,
  deleteRoom,
  deleteRoomAdjustment,
  finalizeAndSaveSiteNotes,
  getProject,
  getProjectTotals,
  listProjectChangeOrders,
  listProjectContacts,
  listProjectPhases,
  listProjectVersionHistory,
  listProjects,
  moveRoomToPhase,
  removeProjectContact,
  reorderPhases,
  reorderRooms,
  returnProjectToDraft,
  setPrimaryProjectContact,
  updatePhase,
  updatePhaseAdjustment,
  updateProjectChangeOrder,
  updateProject,
  updateProjectContact,
  updateRoom,
  updateRoomAdjustment,
} from "../api/projects-api";
import type {
  AddProjectContactPayload,
  CreateChangeOrderPayload,
  CreateAdjustmentPayload,
  CreatePhasePayload,
  CreateProjectPayload,
  CreateRoomPayload,
  ProjectListParams,
  SiteNotesMessage,
  UpdateChangeOrderPayload,
  UpdateAdjustmentPayload,
  UpdatePhasePayload,
  UpdateProjectContactPayload,
  UpdateProjectPayload,
  UpdateRoomPayload,
} from "../types/project.types";

function useInvalidateProjects() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ["projects"] });
}

// ─── Project queries ────────────────────────────────────────────────────

export function useProjectList(params: ProjectListParams = {}) {
  return useQuery({
    queryKey: ["projects", "list", params],
    queryFn: () => listProjects(params),
  });
}

export function useProject(id: number | undefined) {
  return useQuery({
    queryKey: ["projects", "detail", id],
    queryFn: () => getProject(id as number),
    enabled: id !== undefined,
  });
}

export function useProjectSearch(
  search: string,
  opts: { enabled?: boolean; customer?: number } = {},
) {
  const { enabled = true, customer } = opts;
  return useQuery({
    queryKey: ["projects", "search", { search, customer }],
    queryFn: () =>
      listProjects({
        search,
        page_size: 50,
        ordering: "name",
        ...(customer !== undefined ? { customer } : {}),
      }),
    enabled,
  });
}

export function useProjectVersionHistory(projectId: number | undefined) {
  return useQuery({
    queryKey: ["projects", "version-history", projectId],
    queryFn: () => listProjectVersionHistory(projectId as number),
    enabled: projectId !== undefined,
  });
}

export function useProjectChangeOrders(projectId: number | undefined) {
  return useQuery({
    queryKey: ["projects", "change-orders", projectId],
    queryFn: () => listProjectChangeOrders(projectId as number),
    enabled: projectId !== undefined,
  });
}

// ─── Project mutations ──────────────────────────────────────────────────

export function useCreateProject() {
  const invalidate = useInvalidateProjects();
  return useMutation({
    mutationFn: (payload: CreateProjectPayload) => createProject(payload),
    onSuccess: invalidate,
  });
}

export function useUpdateProject() {
  const invalidate = useInvalidateProjects();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: UpdateProjectPayload }) =>
      updateProject(id, payload),
    onSuccess: invalidate,
  });
}

export function useDeleteProject() {
  const invalidate = useInvalidateProjects();
  return useMutation({
    mutationFn: (id: number) => deleteProject(id),
    onSuccess: invalidate,
  });
}

export function useReturnProjectToDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => returnProjectToDraft(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["proposal-versions"] });
      qc.invalidateQueries({ queryKey: ["project-invoices"] });
    },
  });
}

export function useCreateProjectChangeOrder() {
  const invalidate = useInvalidateProjects();
  return useMutation({
    mutationFn: ({
      projectId,
      payload,
    }: {
      projectId: number;
      payload: CreateChangeOrderPayload;
    }) => createProjectChangeOrder(projectId, payload),
    onSuccess: invalidate,
  });
}

export function useUpdateProjectChangeOrder() {
  const invalidate = useInvalidateProjects();
  return useMutation({
    mutationFn: ({
      projectId,
      id,
      payload,
    }: {
      projectId: number;
      id: number;
      payload: UpdateChangeOrderPayload;
    }) => updateProjectChangeOrder(projectId, id, payload),
    onSuccess: invalidate,
  });
}

export function useApproveProjectChangeOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, id }: { projectId: number; id: number }) =>
      approveProjectChangeOrder(projectId, id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["proposal-versions"] });
    },
  });
}

// ─── Project contacts ───────────────────────────────────────────────────

export function useProjectContacts(projectId: number | undefined) {
  return useQuery({
    queryKey: ["projects", "contacts", projectId],
    queryFn: () => listProjectContacts(projectId as number),
    enabled: projectId !== undefined,
  });
}

export function useAddProjectContact() {
  const invalidate = useInvalidateProjects();
  return useMutation({
    mutationFn: ({
      projectId,
      payload,
    }: {
      projectId: number;
      payload: AddProjectContactPayload;
    }) => addProjectContact(projectId, payload),
    onSuccess: invalidate,
  });
}

export function useUpdateProjectContact() {
  const invalidate = useInvalidateProjects();
  return useMutation({
    mutationFn: ({
      projectId,
      id,
      payload,
    }: {
      projectId: number;
      id: number;
      payload: UpdateProjectContactPayload;
    }) => updateProjectContact(projectId, id, payload),
    onSuccess: invalidate,
  });
}

export function useRemoveProjectContact() {
  const invalidate = useInvalidateProjects();
  return useMutation({
    mutationFn: ({ projectId, id }: { projectId: number; id: number }) =>
      removeProjectContact(projectId, id),
    onSuccess: invalidate,
  });
}

export function useSetPrimaryProjectContact() {
  const invalidate = useInvalidateProjects();
  return useMutation({
    mutationFn: ({ projectId, id }: { projectId: number; id: number }) =>
      setPrimaryProjectContact(projectId, id),
    onSuccess: invalidate,
  });
}

// ─── Site-notes finalize-and-save (one click) ───────────────────────────

/** Hits the one-shot endpoint that runs the formatting agent and
 *  writes ``Project.access_site_notes`` in a single backend call.
 *  Replaces the prior two-step (preview → commit) flow which made
 *  users click twice and confused them about whether the first click
 *  had saved. Invalidates the projects tree on success so detail
 *  views pick up the new notes. */
export function useFinalizeAndSaveSiteNotes() {
  const invalidate = useInvalidateProjects();
  return useMutation({
    mutationFn: ({
      projectId,
      history,
      existingNotes,
    }: {
      projectId: number;
      history: SiteNotesMessage[];
      existingNotes: string;
    }) => finalizeAndSaveSiteNotes(projectId, history, existingNotes),
    onSuccess: invalidate,
  });
}

// ─── Phases / Rooms / Adjustments ───────────────────────────────────────

export function useProjectPhases(projectId: number | undefined) {
  return useQuery({
    queryKey: ["projects", "phases", projectId],
    queryFn: () => listProjectPhases(projectId as number),
    enabled: projectId !== undefined,
  });
}

export function useProjectTotals(projectId: number | undefined) {
  return useQuery({
    queryKey: ["projects", "totals", projectId],
    queryFn: () => getProjectTotals(projectId as number),
    enabled: projectId !== undefined,
  });
}

export function useCreatePhase() {
  const invalidate = useInvalidateProjects();
  return useMutation({
    mutationFn: ({
      projectId,
      payload,
    }: {
      projectId: number;
      payload: CreatePhasePayload;
    }) => createPhase(projectId, payload),
    onSuccess: invalidate,
  });
}

export function useUpdatePhase() {
  const invalidate = useInvalidateProjects();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: UpdatePhasePayload }) =>
      updatePhase(id, payload),
    onSuccess: invalidate,
  });
}

export function useDeletePhase() {
  const invalidate = useInvalidateProjects();
  return useMutation({
    mutationFn: ({ id }: { id: number }) => deletePhase(id),
    onSuccess: invalidate,
  });
}

export function useReorderPhases() {
  const invalidate = useInvalidateProjects();
  return useMutation({
    mutationFn: ({
      projectId,
      orderedIds,
    }: {
      projectId: number;
      orderedIds: number[];
    }) => reorderPhases(projectId, orderedIds),
    onSuccess: invalidate,
  });
}

export function useCreateRoom() {
  const invalidate = useInvalidateProjects();
  return useMutation({
    mutationFn: ({
      phaseId,
      payload,
    }: {
      phaseId: number;
      payload: CreateRoomPayload;
    }) => createRoom(phaseId, payload),
    onSuccess: invalidate,
  });
}

export function useUpdateRoom() {
  const invalidate = useInvalidateProjects();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: UpdateRoomPayload }) =>
      updateRoom(id, payload),
    onSuccess: invalidate,
  });
}

export function useDeleteRoom() {
  const invalidate = useInvalidateProjects();
  return useMutation({
    mutationFn: ({ id }: { id: number }) => deleteRoom(id),
    onSuccess: invalidate,
  });
}

export function useReorderRooms() {
  const invalidate = useInvalidateProjects();
  return useMutation({
    mutationFn: ({
      phaseId,
      orderedIds,
    }: {
      phaseId: number;
      orderedIds: number[];
    }) => reorderRooms(phaseId, orderedIds),
    onSuccess: invalidate,
  });
}

export function useMoveRoomToPhase() {
  const invalidate = useInvalidateProjects();
  return useMutation({
    mutationFn: ({
      roomId,
      targetPhaseId,
    }: {
      roomId: number;
      targetPhaseId: number;
    }) => moveRoomToPhase(roomId, targetPhaseId),
    onSuccess: invalidate,
  });
}

export function useCreateRoomAdjustment() {
  const invalidate = useInvalidateProjects();
  return useMutation({
    mutationFn: ({
      roomId,
      payload,
    }: {
      roomId: number;
      payload: CreateAdjustmentPayload;
    }) => createRoomAdjustment(roomId, payload),
    onSuccess: invalidate,
  });
}

export function useUpdateRoomAdjustment() {
  const invalidate = useInvalidateProjects();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: number;
      payload: UpdateAdjustmentPayload;
    }) => updateRoomAdjustment(id, payload),
    onSuccess: invalidate,
  });
}

export function useDeleteRoomAdjustment() {
  const invalidate = useInvalidateProjects();
  return useMutation({
    mutationFn: ({ id }: { id: number }) => deleteRoomAdjustment(id),
    onSuccess: invalidate,
  });
}

export function useCreatePhaseAdjustment() {
  const invalidate = useInvalidateProjects();
  return useMutation({
    mutationFn: ({
      phaseId,
      payload,
    }: {
      phaseId: number;
      payload: CreateAdjustmentPayload;
    }) => createPhaseAdjustment(phaseId, payload),
    onSuccess: invalidate,
  });
}

export function useUpdatePhaseAdjustment() {
  const invalidate = useInvalidateProjects();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: number;
      payload: UpdateAdjustmentPayload;
    }) => updatePhaseAdjustment(id, payload),
    onSuccess: invalidate,
  });
}

export function useDeletePhaseAdjustment() {
  const invalidate = useInvalidateProjects();
  return useMutation({
    mutationFn: ({ id }: { id: number }) => deletePhaseAdjustment(id),
    onSuccess: invalidate,
  });
}
